/**
 * Unified Chart Color System
 * Apple-quality design with purple accents and sophisticated color palette
 * Optimized for both light and dark modes with excellent accessibility
 */

export const CHART_COLORS = {
  // Primary purple theme colors
  PRIMARY: {
    // Deep purple for primary elements
    PURPLE_DARK: '#6366F1',     // Indigo-600
    PURPLE_LIGHT: '#8B5CF6',    // Violet-500
    
    // Subtle background purples
    PURPLE_SUBTLE_DARK: '#4C1D95',   // Violet-900
    PURPLE_SUBTLE_LIGHT: '#EDE9FE',  // Violet-100
  },

  // Data visualization colors - sophisticated but semantically correct
  DATA: {
    // Positive values (success/growth) - matches theme primary green
    POSITIVE_DARK: '#22c55e',   // Primary green
    POSITIVE_LIGHT: '#16a34a',  // Primary green dark

    // Negative values (decline/loss)
    NEGATIVE_DARK: '#EF4444',   // Red-500 (refined red)
    NEGATIVE_LIGHT: '#DC2626',  // Red-600

    // Neutral values
    NEUTRAL_DARK: '#6B7280',    // Gray-500
    NEUTRAL_LIGHT: '#4B5563',   // Gray-600

    // Warning/caution
    WARNING_DARK: '#F59E0B',    // Amber-500
    WARNING_LIGHT: '#D97706',   // Amber-600

    // Accent (non-semantic) - distinct violet for charts that need to differ
    // from the positive/negative palette (e.g. P/FCF chart).
    ACCENT_DARK: '#A78BFA',     // Violet-400 (bright on dark backgrounds)
    ACCENT_LIGHT: '#7C3AED',    // Violet-600 (deep on light backgrounds)
  },

  // Background gradients with purple hints
  BACKGROUND: {
    CARD_DARK: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(124, 58, 237, 0.03) 100%), rgba(26, 26, 26, 0.95)',
    CARD_LIGHT: 'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.03) 50%, rgba(124, 58, 237, 0.02) 100%), rgba(248, 249, 250, 0.98)',
  },

  // Border colors
  BORDER: {
    DARK: 'rgba(99, 102, 241, 0.15)',
    LIGHT: 'rgba(99, 102, 241, 0.1)',
  },

  // Text gradients
  TEXT_GRADIENT: {
    DARK: 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.8) 100%)',
    LIGHT: 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.8) 100%)',
  },

  // Shadow effects
  SHADOW: {
    DARK: '0 8px 32px rgba(99, 102, 241, 0.08)',
    LIGHT: '0 8px 32px rgba(99, 102, 241, 0.06)',
  },

  // Toggle button colors
  TOGGLE: {
    BORDER_DARK: 'rgba(99, 102, 241, 0.2)',
    BORDER_LIGHT: 'rgba(99, 102, 241, 0.2)',
    COLOR_DARK: 'rgba(99, 102, 241, 0.8)',
    COLOR_LIGHT: 'rgba(99, 102, 241, 0.8)',
    SELECTED_BG_DARK: 'rgba(99, 102, 241, 0.1)',
    SELECTED_BG_LIGHT: 'rgba(99, 102, 241, 0.1)',
    SELECTED_COLOR_DARK: 'rgba(99, 102, 241, 1)',
    SELECTED_COLOR_LIGHT: 'rgba(99, 102, 241, 1)',
  },

  // Growth metrics (keeping the established green/red but refined)
  GROWTH: {
    POSITIVE_BG: 'rgba(16, 185, 129, 0.1)',
    POSITIVE_COLOR: '#10B981',
    POSITIVE_BORDER: 'rgba(16, 185, 129, 0.3)',
    
    NEGATIVE_BG: 'rgba(239, 68, 68, 0.1)',
    NEGATIVE_COLOR: '#EF4444',
    NEGATIVE_BORDER: 'rgba(239, 68, 68, 0.3)',
  },

  // Special colors for specific chart types
  SPECIAL: {
    // Altman Z-Score zones
    ALTMAN_SAFE: '#10B981',      // Emerald-500
    ALTMAN_MODERATE: '#F59E0B',  // Amber-500
    ALTMAN_RISK: '#EF4444',      // Red-500
    
    // Piotroski Score
    PIOTROSKI_EXCELLENT: '#10B981',  // Emerald-500
    PIOTROSKI_GOOD: '#84CC16',       // Lime-500
    PIOTROSKI_FAIR: '#F59E0B',       // Amber-500
    PIOTROSKI_POOR: '#EF4444',       // Red-500
  },
};

/**
 * Get chart colors based on theme mode
 */
export const getChartColors = (isDarkMode: boolean) => ({
  background: isDarkMode ? CHART_COLORS.BACKGROUND.CARD_DARK : CHART_COLORS.BACKGROUND.CARD_LIGHT,
  border: isDarkMode ? CHART_COLORS.BORDER.DARK : CHART_COLORS.BORDER.LIGHT,
  textGradient: isDarkMode ? CHART_COLORS.TEXT_GRADIENT.DARK : CHART_COLORS.TEXT_GRADIENT.LIGHT,
  shadow: isDarkMode ? CHART_COLORS.SHADOW.DARK : CHART_COLORS.SHADOW.LIGHT,
  positive: isDarkMode ? CHART_COLORS.DATA.POSITIVE_DARK : CHART_COLORS.DATA.POSITIVE_LIGHT,
  negative: isDarkMode ? CHART_COLORS.DATA.NEGATIVE_DARK : CHART_COLORS.DATA.NEGATIVE_LIGHT,
  neutral: isDarkMode ? CHART_COLORS.DATA.NEUTRAL_DARK : CHART_COLORS.DATA.NEUTRAL_LIGHT,
  warning: isDarkMode ? CHART_COLORS.DATA.WARNING_DARK : CHART_COLORS.DATA.WARNING_LIGHT,
  accent: isDarkMode ? CHART_COLORS.DATA.ACCENT_DARK : CHART_COLORS.DATA.ACCENT_LIGHT,
  
  toggle: {
    border: isDarkMode ? CHART_COLORS.TOGGLE.BORDER_DARK : CHART_COLORS.TOGGLE.BORDER_LIGHT,
    color: isDarkMode ? CHART_COLORS.TOGGLE.COLOR_DARK : CHART_COLORS.TOGGLE.COLOR_LIGHT,
    selectedBg: isDarkMode ? CHART_COLORS.TOGGLE.SELECTED_BG_DARK : CHART_COLORS.TOGGLE.SELECTED_BG_LIGHT,
    selectedColor: isDarkMode ? CHART_COLORS.TOGGLE.SELECTED_COLOR_DARK : CHART_COLORS.TOGGLE.SELECTED_COLOR_LIGHT,
  },
});

/**
 * Ordered categorical palette for multi-series comparison charts (AI-chat Tier-2).
 * Green → violet → amber → indigo → grey; callers cycle with modulo for more
 * series/slices. Red is deliberately EXCLUDED — it is semantic (negative values)
 * across the app and must not become a company's identity colour in a comparison.
 */
export const getSeriesPalette = (isDarkMode: boolean): string[] => [
  isDarkMode ? CHART_COLORS.DATA.POSITIVE_DARK : CHART_COLORS.DATA.POSITIVE_LIGHT,
  isDarkMode ? CHART_COLORS.DATA.ACCENT_DARK : CHART_COLORS.DATA.ACCENT_LIGHT,
  isDarkMode ? CHART_COLORS.DATA.WARNING_DARK : CHART_COLORS.DATA.WARNING_LIGHT,
  isDarkMode ? CHART_COLORS.PRIMARY.PURPLE_DARK : CHART_COLORS.PRIMARY.PURPLE_LIGHT,
  isDarkMode ? CHART_COLORS.DATA.NEUTRAL_DARK : CHART_COLORS.DATA.NEUTRAL_LIGHT,
];

/**
 * Extended categorical palette for many-slice charts (portfolio donuts), used
 * after the theme's primary, info and warning colours. Red is excluded for the
 * same reason as getSeriesPalette.
 */
export const PORTFOLIO_SERIES_COLORS: string[] = [
  '#a78bfa',
  '#22d3ee',
  '#f472b6',
  '#fb923c',
  '#818cf8',
  '#2dd4bf',
  '#facc15',
  '#e879f9',
  '#60a5fa',
  '#a3e635',
  '#fb7185',
  '#c4b5fd',
  '#67e8f9',
  '#fdba74',
  '#5eead4',
  '#bef264',
  '#f0abfc',
];

/**
 * Get semantic colors for data visualization
 */
export const getSemanticColors = (isDarkMode: boolean) => ({
  success: isDarkMode ? CHART_COLORS.DATA.POSITIVE_DARK : CHART_COLORS.DATA.POSITIVE_LIGHT,
  error: isDarkMode ? CHART_COLORS.DATA.NEGATIVE_DARK : CHART_COLORS.DATA.NEGATIVE_LIGHT,
  warning: isDarkMode ? CHART_COLORS.DATA.WARNING_DARK : CHART_COLORS.DATA.WARNING_LIGHT,
  info: isDarkMode ? CHART_COLORS.PRIMARY.PURPLE_DARK : CHART_COLORS.PRIMARY.PURPLE_LIGHT,
});

/**
 * Purple-themed icon colors
 */
export const getIconColors = (isDarkMode: boolean) => ({
  primary: isDarkMode ? CHART_COLORS.PRIMARY.PURPLE_DARK : CHART_COLORS.PRIMARY.PURPLE_LIGHT,
  secondary: isDarkMode ? CHART_COLORS.DATA.NEUTRAL_DARK : CHART_COLORS.DATA.NEUTRAL_LIGHT,
});